import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as OrgIcon,
  Folder as ProjectIcon,
  ViewKanban as BoardIcon,
  Celebration as CelebrationIcon,
  CorporateFare as WorkspaceIcon,
} from '@mui/icons-material';
import api from '@/api/axios';
import { ENDPOINTS } from '@/api/endpoints';
import { useOrgStore } from '@/store';

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onCreateWorkspace: (name: string, organizationId?: string) => Promise<void>;
}

const steps = ['Welcome', 'Organization', 'Workspace', 'Get Started'];

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  open,
  onClose,
  onCreateWorkspace,
}) => {
  const { addOrg, setCurrentOrgId } = useOrgStore();
  const [activeStep, setActiveStep] = useState(0);
  const [orgName, setOrgName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateOrg = async () => {
    if (!orgName.trim()) return;
    setIsCreatingOrg(true);
    setError(null);
    try {
      const res = await api.post(ENDPOINTS.ORGANIZATIONS.CREATE, {
        name: orgName.trim(),
      });
      const org = res.data;
      const orgId = org?.organizationId || org?.organization_id;
      setOrganizationId(orgId);
      // Push into store so OrgSelector updates immediately
      addOrg(org);
      setCurrentOrgId(orgId);
      setActiveStep(2);
    } catch (err: any) {
      setError(err?.message || 'Failed to create organization. Please try again.');
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) return;
    setIsCreatingWorkspace(true);
    setError(null);
    try {
      await onCreateWorkspace(workspaceName.trim(), organizationId);
      setActiveStep(3);
    } catch (err: any) {
      setError(err?.message || 'Failed to create workspace. Please try again.');
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      handleCreateOrg();
    } else if (activeStep === 2) {
      handleCreateWorkspace();
    } else {
      onClose();
    }
  };

  const isNextDisabled = () => {
    if (activeStep === 1) return !orgName.trim() || isCreatingOrg;
    if (activeStep === 2) return !workspaceName.trim() || isCreatingWorkspace;
    return false;
  };

  const nextLabel = () => {
    if (activeStep === 0) return 'Get Started';
    if (activeStep === 1) return isCreatingOrg ? 'Creating...' : 'Create Organization';
    if (activeStep === 2) return isCreatingWorkspace ? 'Creating...' : 'Create Workspace';
    return 'Go to Dashboard';
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0 — Welcome */}
        {activeStep === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
              <BoardIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Welcome to Track2Excel
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Your project management tool for teams. Let's set up your organization and workspace in under a minute.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              {[
                { icon: <OrgIcon />, label: 'Organize projects' },
                { icon: <ProjectIcon />, label: 'Track issues' },
                { icon: <BoardIcon />, label: 'Kanban boards' },
              ].map((item) => (
                <Paper key={item.label} variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.100', color: 'primary.main', mx: 'auto', mb: 1 }}>
                    {item.icon}
                  </Avatar>
                  <Typography variant="caption">{item.label}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Step 1 — Create Organization */}
        {activeStep === 1 && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <OrgIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Create your Organization</Typography>
                <Typography variant="body2" color="text.secondary">
                  An organization groups your workspaces and manages billing.
                </Typography>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              value={orgName}
              onChange={(e) => { setOrgName(e.target.value); setError(null); }}
              label="Organization name"
              placeholder="e.g. Acme Corp, My Company"
              fullWidth
              autoFocus
              disabled={isCreatingOrg}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateOrg(); }}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              This is your company or team name. You can change it later.
            </Typography>
          </Box>
        )}

        {/* Step 2 — Create Workspace */}
        {activeStep === 2 && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <WorkspaceIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>Create your first Workspace</Typography>
                <Typography variant="body2" color="text.secondary">
                  A workspace contains your projects and team members.
                </Typography>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              value={workspaceName}
              onChange={(e) => { setWorkspaceName(e.target.value); setError(null); }}
              label="Workspace name"
              placeholder="e.g. Product Team, Engineering, Marketing"
              fullWidth
              autoFocus
              disabled={isCreatingWorkspace}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateWorkspace(); }}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              You can create multiple workspaces for different teams.
            </Typography>
          </Box>
        )}

        {/* Step 3 — Done */}
        {activeStep === 3 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
              <CelebrationIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You're all set!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              <strong>{orgName}</strong> and workspace <strong>{workspaceName}</strong> are ready.
            </Typography>
            <Box sx={{ textAlign: 'left', maxWidth: 300, mx: 'auto' }}>
              {[
                'Create your first project',
                'Invite team members',
                'Create issues and track progress',
                'Use Kanban boards and sprints',
              ].map((text, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.7rem' }}>{i + 1}</Avatar>
                  <Typography variant="body2">{text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Box>
            {activeStep === 0 && (
              <Button variant="text" color="inherit" onClick={onClose}>
                Skip for now
              </Button>
            )}
            {activeStep > 0 && activeStep < 3 && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => { setActiveStep((p) => p - 1); setError(null); }}
                disabled={isCreatingOrg || isCreatingWorkspace}
              >
                Back
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={handleNext}
            disabled={isNextDisabled()}
            startIcon={(isCreatingOrg || isCreatingWorkspace) ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ minWidth: 160 }}
          >
            {nextLabel()}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
